import api from '@/services/Api';
import Arbeitsplatz from '@/types/Arbeitsplatz';
import { successSwal } from '@/helper/swals';
import { ActionContext, Module } from 'vuex';
import { ArbeitsplaetzeState, RootState } from '@/store/types';
import jspm from '@/services/PrintManager';

type Context = ActionContext<ArbeitsplaetzeState, RootState>;

export const arbeitsplaetze: Module<ArbeitsplaetzeState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            edit: false,
        },
        loaders: {
            save: false,
            delete: false,
            arbeitsplaetze: false,
            printers: false,
        },
        arbeitsplaetze: [],
        printers: [],
    },
    actions: {
        loadArbeitsplaetze({ commit, rootState }: Context): void {
            commit('setLoadersArbeitsplaetze', true);

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
            };

            api.post('verwaltung/arbeitsplaetze/load', data)
                .then((response) => commit('setArbeitsplaetze', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoadersArbeitsplaetze', false));
        },
        updateArbeitsplatz({ commit, dispatch }: Context, payload: Arbeitsplatz): void {
            commit('setLoadersSave', true);

            api.post('verwaltung/arbeitsplaetze/speichern', payload)
                .then(() => {
                    successSwal('Arbeitsplatz', 'gespeichert');
                    dispatch('loadArbeitsplaetze');
                    dispatch('authentication/loadImpfstelle', null, { root: true });
                    commit('setDialogsDetail', false);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersSave', false));
        },
        deleteArbeitsplatz({ commit, dispatch }: Context, payload: Arbeitsplatz): void {
            commit('setLoadersDelete', true);

            api.post('verwaltung/arbeitsplaetze/loeschen', payload)
                .then(() => {
                    successSwal('Arbeitsplatz', 'gelöscht');
                    dispatch('loadArbeitsplaetze');
                    dispatch('authentication/loadImpfstelle', null, { root: true });
                    commit('setDialogsDetail', false);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersDelete', false));
        },
        loadPrinters({ commit, dispatch }: Context): void {
            if (!jspm.printer?.hasConnected()) {
                dispatch('globals/setDialogDruckhinweis', true, { root: true });
                return;
            }

            const manager = jspm.printer?.manager;
            if (!manager) {
                dispatch('globals/setDialogDruckhinweis', true, { root: true });
                return;
            }

            const { JSPrintManager, WSStatus } = manager;

            if (JSPrintManager.websocket_status !== WSStatus.Open) {
                dispatch('globals/setDialogDruckhinweis', true, { root: true });
                return;
            }

            JSPrintManager
                .getPrinters()
                .then(async(printers: string[]) => {
                    if (printers?.length) {
                        commit('setPrinters', printers);
                        return;
                    }

                    const printerInfo = await JSPrintManager.getPrintersInfo();
                    const detailedPrinters = Array.isArray(printerInfo)
                        ? printerInfo
                        : [];

                    const fallbackNames = detailedPrinters
                        .map((item: unknown) => {
                            if (!item || typeof item !== 'object') {
                                return null;
                            }

                            const record = item as Record<string, unknown>;
                            const value =
                                record.name ??
                                record.printerName ??
                                record.PrinterName ??
                                record.queueName ??
                                record.QueueName;

                            return typeof value === 'string' ? value : null;
                        })
                        .filter((value): value is string => Boolean(value));

                    commit('setPrinters', fallbackNames);
                    console.log('Detailed printer info', { printerInfo, fallbackNames });
                })
                .catch(console.error);
        },
    },
    mutations: {
        setArbeitsplaetze(state: ArbeitsplaetzeState, payload: Arbeitsplatz[]): void {
            state.arbeitsplaetze = payload.map((data: Arbeitsplatz) => new Arbeitsplatz(data));
        },
        setDialogsDetail(state: ArbeitsplaetzeState, visible: boolean): void {
            state.dialogs.edit = visible;
        },
        setLoadersSave(state: ArbeitsplaetzeState, loading: boolean): void {
            state.loaders.save = loading;
        },
        setLoadersDelete(state: ArbeitsplaetzeState, loading: boolean): void {
            state.loaders.delete = loading;
        },
        setLoadersArbeitsplaetze(state: ArbeitsplaetzeState, loading: boolean): void {
            state.loaders.arbeitsplaetze = loading;
        },
        setLoadersPrinters(state: ArbeitsplaetzeState, loading: boolean): void {
            state.loaders.printers = loading;
        },
        setPrinters(state: ArbeitsplaetzeState, printers: string[]): void {
            state.printers = printers;
        },
    },
};
